//!nordy - promiseTimeout, until 미사용 (waitForAuthInitialization 주석 처리 후)
// import { promiseTimeout, until } from "@vueuse/core";
import axios from 'axios'
//!nordy - storeToRefs 미사용 (waitForAuthInitialization 주석 처리 후)
// import { storeToRefs } from 'pinia'
import { get } from 'es-toolkit/compat'
import { trimEnd } from 'es-toolkit'
import { ref } from 'vue'

import { nordyApi } from '@/nordy/api'
import { createClientSessionId } from '@/nordy/utils/createClientSessionId'
import { isObjectId } from '@/nordy/utils/isObjectId'

import defaultClientFeatureFlags from '@/config/clientFeatureFlags.json' with { type: 'json' }
import { getDevOverride } from '@/utils/devFeatureFlagOverride'
import type {
  ModelFile,
  ModelFolderInfo
} from '@/platform/assets/schemas/assetSchema'
import { isCloud } from '@/platform/distribution/types'
import { useToastStore } from '@/platform/updates/common/toastStore'
import type { ShareableAssetsResponse } from '@/schemas/apiSchema'
import { zShareableAssetsResponse } from '@/schemas/apiSchema'
import type { IFuseOptions } from 'fuse.js'
import type {
  TemplateIncludeOnDistributionEnum,
  TemplateInfo,
  WorkflowTemplates
} from '@/platform/workflow/templates/types/template'
import type {
  ComfyApiWorkflow,
  ComfyWorkflowJSON,
  NodeId
} from '@/platform/workflow/validation/schemas/workflowSchema'
import type {
  AssetDownloadWsMessage,
  AssetExportWsMessage,
  CustomNodesI18n,
  EmbeddingsResponse,
  ExecutedWsMessage,
  ExecutingWsMessage,
  ExecutionCachedWsMessage,
  ExecutionErrorWsMessage,
  ExecutionInterruptedWsMessage,
  ExecutionStartWsMessage,
  ExecutionSuccessWsMessage,
  ExtensionsResponse,
  FeatureFlagsWsMessage,
  LogsRawResponse,
  LogsWsMessage,
  NotificationWsMessage,
  PreviewMethod,
  ProgressStateWsMessage,
  ProgressTextWsMessage,
  ProgressWsMessage,
  PromptResponse,
  Settings,
  StatusWsMessage,
  StatusWsMessageStatus,
  SystemStats,
  User,
  UserDataFullInfo
} from '@/schemas/apiSchema'
import type {
  JobDetail,
  JobListItem
} from '@/platform/remote/comfyui/jobs/jobTypes'
import type { ComfyNodeDef } from '@/schemas/nodeDefSchema'
//!nordy - useFirebaseAuthStore 현재 미사용, getAuthStore를 위해 유지
// import type { useFirebaseAuthStore } from "@/stores/firebaseAuthStore";
//!nordy - AuthHeader 현재 미사용, nordy 인증 플로우를 위해 유지
// import type { AuthHeader } from "@/types/authTypes";
import type { NodeExecutionId } from '@/types/nodeIdentification'
//!nordy - fetchHistory, fetchQueue 현재 미사용, nordy 큐 플로우를 위해 유지
import {
  // fetchHistory,
  fetchJobDetail
  // fetchQueue,
} from '@/platform/remote/comfyui/jobs/fetchJobs'

//!nordy - QueuePromptRequestBody 현재 미사용, nordy는 별도 큐 플로우 사용
// interface QueuePromptRequestBody {
//   client_id: string;
//   prompt: ComfyApiWorkflow;
//   partial_execution_targets?: NodeExecutionId[];
//   extra_data: {
//     extra_pnginfo: {
//       workflow: ComfyWorkflowJSON;
//     };
//     auth_token_comfy_org?: string;
//     api_key_comfy_org?: string;
//     preview_method?: PreviewMethod;
//   };
//   front?: boolean;
//   number?: number;
// }

/**
 * Options for queuePrompt method
 */
interface QueuePromptOptions {
  /**
   * Optional list of node execution IDs to execute (partial execution).
   * Each ID represents a node's position in nested subgraphs.
   * Format: Colon-separated path of node IDs (e.g., "123:456:789")
   */
  partialExecutionTargets?: NodeExecutionId[]
  /**
   * Override the preview method for this prompt execution.
   * 'default' uses the server's CLI setting and is not sent to backend.
   */
  previewMethod?: PreviewMethod
  pendingTaskId?: string
}

/** Dictionary of Frontend-generated API calls */
interface FrontendApiCalls {
  graphChanged: ComfyWorkflowJSON
  promptQueueing: { requestId: number; batchCount: number; number?: number }
  promptQueued: { number: number; batchCount: number; requestId?: number }
  graphCleared: never
  reconnecting: never
  reconnected: never
}

export type PromptQueueingEventPayload = FrontendApiCalls['promptQueueing']
export type PromptQueuedEventPayload = FrontendApiCalls['promptQueued']

/** Dictionary of calls originating from ComfyUI core */
interface BackendApiCalls {
  progress: ProgressWsMessage
  executing: ExecutingWsMessage
  executed: ExecutedWsMessage
  status: StatusWsMessage
  notification: NotificationWsMessage
  execution_start: ExecutionStartWsMessage
  execution_success: ExecutionSuccessWsMessage
  execution_error: ExecutionErrorWsMessage
  execution_interrupted: ExecutionInterruptedWsMessage
  execution_cached: ExecutionCachedWsMessage
  logs: LogsWsMessage
  /** Binary preview/progress data */
  b_preview: Blob
  /** Binary preview with metadata (node_id, job_id) */
  b_preview_with_metadata: {
    blob: Blob
    nodeId: string
    parentNodeId: string
    displayNodeId: string
    realNodeId: string
    jobId: string
  }
  progress_text: ProgressTextWsMessage
  progress_state: ProgressStateWsMessage
  feature_flags: FeatureFlagsWsMessage
  asset_download: AssetDownloadWsMessage
  asset_export: AssetExportWsMessage
}

/** Dictionary of all api calls */
interface ApiCalls extends BackendApiCalls, FrontendApiCalls {}

/** Used to create a discriminating union on type value. */
interface ApiMessage<T extends keyof ApiCalls> {
  type: T
  data: ApiCalls[T]
}

export class UnauthorizedError extends Error {}

/** Ensures workers get a fair shake. */
type Unionize<T> = T[keyof T]

/**
 *  Discriminated union of generic, i.e.:
 * ```ts
 * // Convert
 * type ApiMessageUnion = ApiMessage<'status' | 'executing' | ...>
 * // To
 * type ApiMessageUnion = ApiMessage<'status'> | ApiMessage<'executing'> | ...
 * ```
 */
type ApiMessageUnion = Unionize<{
  [Key in keyof ApiCalls]: ApiMessage<Key>
}>

/** Wraps all properties in {@link CustomEvent}. */
type AsCustomEvents<T> = {
  readonly [K in keyof T]: CustomEvent<T[K]>
}

/** Handles differing event and API signatures. */
type ApiToEventType<T = ApiCalls> = {
  [K in keyof T]: K extends 'status'
    ? StatusWsMessageStatus
    : K extends 'executing'
      ? NodeId
      : T[K]
}

/** Dictionary of types used in the detail for a custom event */
type ApiEventTypes = ApiToEventType<ApiCalls>

/** Dictionary of API events: `[name]: CustomEvent<Type>` */
type ApiEvents = AsCustomEvents<ApiEventTypes>

/** {@link Omit} all properties that evaluate to `never`. */
type NeverNever<T> = {
  [K in keyof T as T[K] extends never ? never : K]: T[K]
}

/** {@link Pick} only properties that evaluate to `never`. */
type PickNevers<T> = {
  [K in keyof T as T[K] extends never ? K : never]: T[K]
}

/** Keys (names) of API events that _do not_ pass a {@link CustomEvent} `detail` object. */
type SimpleApiEvents = keyof PickNevers<ApiEventTypes>
/** Keys (names) of API events that pass a {@link CustomEvent} `detail` object. */
type ComplexApiEvents = keyof NeverNever<ApiEventTypes>

export type GlobalSubgraphData = {
  name: string
  info: {
    node_pack: string
    category?: string
    search_aliases?: string[]
    requiresCustomNodes?: string[]
    includeOnDistributions?: TemplateIncludeOnDistributionEnum[]
  }
  data: string | Promise<string>
  essentials_category?: string
}

//!nordy - addHeaderEntry 현재 미사용, nordy 인증 헤더 주입을 위해 유지
// function addHeaderEntry(headers: HeadersInit, key: string, value: string) {
//   if (Array.isArray(headers)) {
//     headers.push([key, value]);
//   } else if (headers instanceof Headers) {
//     headers.set(key, value);
//   } else {
//     headers[key] = value;
//   }
// }

/** EventTarget typing has no generic capability. */
export interface ComfyApi extends EventTarget {
  addEventListener<TEvent extends keyof ApiEvents>(
    type: TEvent,
    callback: ((event: ApiEvents[TEvent]) => void) | null,
    options?: AddEventListenerOptions | boolean
  ): void

  removeEventListener<TEvent extends keyof ApiEvents>(
    type: TEvent,
    callback: ((event: ApiEvents[TEvent]) => void) | null,
    options?: EventListenerOptions | boolean
  ): void
}

export class PromptExecutionError extends Error {
  response: PromptResponse
  status?: number

  constructor(response: PromptResponse, status?: number) {
    super('Prompt execution failed')
    this.response = response
    this.status = status
  }

  override toString() {
    let message = ''
    if (typeof this.response.error === 'string') {
      message += this.response.error
    } else if (this.response.error) {
      message +=
        this.response.error.message + ': ' + this.response.error.details
    }

    for (const [_, nodeError] of Object.entries(
      this.response.node_errors ?? []
    )) {
      message += '\n' + nodeError.class_type + ':'
      for (const errorReason of nodeError.errors) {
        message += '\n    - ' + errorReason.message + ': ' + errorReason.details
      }
    }

    return message
  }
}

export class ComfyApi extends EventTarget {
  private _registered = new Set()
  api_host: string
  api_base: string
  /**
   * The client id from the initial session storage.
   */
  initialClientId: string | null
  /**
   * The current client id from websocket status updates.
   */
  clientId?: string
  /**
   * The current user id.
   */
  user: string
  socket: WebSocket | null = null

  //!nordy - authStoreComposable 현재 미사용, getAuthStore를 위해 유지
  // private authStoreComposable?: typeof useFirebaseAuthStore;

  reportedUnknownMessageTypes = new Set<string>()

  nordyApiKey: string | undefined = undefined

  private socketEventHandlers: {
    open?: (event: Event) => void
    close?: (event: CloseEvent) => void
    error?: (event: Event) => void
    message?: (event: MessageEvent) => void
  } = {}

  /**
   * Get feature flags supported by this frontend client.
   * Returns a copy to prevent external modification.
   */
  getClientFeatureFlags(): Record<string, unknown> {
    return { ...defaultClientFeatureFlags }
  }

  /**
   * Feature flags received from the backend server.
   */
  serverFeatureFlags = ref<Record<string, unknown>>({})

  /**
   * The auth token for the comfy org account if the user is logged in.
   * This is only used for {@link queuePrompt} now. It is not directly
   * passed as parameter to the function because some custom nodes are hijacking
   * {@link queuePrompt} improperly, which causes extra parameters to be lost
   * in the function call chain.
   *
   * Ref: https://cs.comfy.org/search?q=context:global+%22api.queuePrompt+%3D%22&patternType=keyword&sm=0
   *
   * TODO: Move this field to parameter of {@link queuePrompt} once all
   * custom nodes are patched.
   */
  authToken?: string
  /**
   * The API key for the comfy org account if the user logged in via API key.
   */
  apiKey?: string

  constructor() {
    super()
    this.user = ''
    this.api_host = import.meta.env.VITE_COMFYUI_URL ?? ''
    this.api_base = import.meta.env.VITE_COMFYUI_URL ?? ''
    this.initialClientId = createClientSessionId()
    this.clientId = this.initialClientId
  }

  setNordyApiKey(nordyApiKey: string | undefined) {
    if (nordyApiKey) {
      this.nordyApiKey = nordyApiKey
    }
  }

  internalURL(route: string): string {
    return this.api_base + '/internal' + route
  }

  apiURL(route: string): string {
    if (route.startsWith('/api')) return this.api_base + route
    return this.api_base + '/api' + route
  }

  fileURL(route: string): string {
    return this.api_base + route
  }

  //!nordy - getAuthStore 현재 미사용, waitForAuthInitialization을 위해 유지
  // private async getAuthStore() {
  //   if (isCloud) {
  //     if (!this.authStoreComposable) {
  //       const module = await import("@/stores/firebaseAuthStore");
  //       this.authStoreComposable = module.useFirebaseAuthStore;
  //     }
  //
  //     return this.authStoreComposable();
  //   }
  // }

  //!nordy - waitForAuthInitialization 현재 미사용, nordy는 별도 인증 플로우 사용
  // private async waitForAuthInitialization(): Promise<void> {
  //   if (isCloud) {
  //     const authStore = await this.getAuthStore();
  //     if (!authStore) return;
  //
  //     if (authStore.isInitialized) return;
  //
  //     const { isInitialized } = storeToRefs(authStore)
  //     try {
  //       await Promise.race([
  //         until(isInitialized).toBe(true),
  //         promiseTimeout(10000),
  //       ]);
  //     } catch {
  //       console.warn("Firebase auth initialization timeout after 10 seconds");
  //     }
  //   }
  // }

  async fetchApi(route: string, options?: RequestInit) {
    if (!options) {
      options = {}
    }
    if (!options.headers) {
      options.headers = {}
    }
    if (!options.cache) {
      options.cache = 'no-cache'
    }

    if (Array.isArray(options.headers)) {
      options.headers.push(['Comfy-User', this.user])
      options.headers.push(['x-origin-url', window.location.href])
      if (this.nordyApiKey) {
        options.headers.push(['api-key', this.nordyApiKey])
      }
    } else if (options.headers instanceof Headers) {
      options.headers.set('Comfy-User', this.user)
      options.headers.set('x-origin-url', window.location.href)
      if (this.nordyApiKey) {
        options.headers.set('api-key', this.nordyApiKey)
      }
    } else {
      options.headers['Comfy-User'] = this.user
      options.headers['x-origin-url'] = window.location.href
      if (this.nordyApiKey) {
        options.headers['api-key'] = this.nordyApiKey
      }
    }

    return fetch(this.apiURL(route), options)
  }

  override addEventListener<TEvent extends keyof ApiEvents>(
    type: TEvent,
    callback: ((event: ApiEvents[TEvent]) => void) | null,
    options?: AddEventListenerOptions | boolean
  ) {
    // Type assertion: strictFunctionTypes.  So long as we emit events in a type-safe fashion, this is safe.
    super.addEventListener(type, callback as EventListener, options)
    this._registered.add(type)
  }

  override removeEventListener<TEvent extends keyof ApiEvents>(
    type: TEvent,
    callback: ((event: ApiEvents[TEvent]) => void) | null,
    options?: EventListenerOptions | boolean
  ): void {
    super.removeEventListener(type, callback as EventListener, options)
  }

  addCustomEventListener(
    type: string,
    callback: ((event: CustomEvent<unknown>) => void) | null,
    options?: AddEventListenerOptions | boolean
  ) {
    super.addEventListener(type, callback as EventListener, options)
    this._registered.add(type)
  }

  removeCustomEventListener(
    type: string,
    callback: ((event: CustomEvent<unknown>) => void) | null,
    options?: EventListenerOptions | boolean
  ) {
    super.removeEventListener(type, callback as EventListener, options)
  }

  /**
   * Dispatches a custom event.
   * Provides type safety for the contravariance issue with EventTarget (last checked TS 5.6).
   * @param type The type of event to emit
   * @param detail The detail property used for a custom event ({@link CustomEventInit.detail})
   */
  dispatchCustomEvent<T extends SimpleApiEvents>(type: T): boolean
  dispatchCustomEvent<T extends ComplexApiEvents>(
    type: T,
    detail: ApiEventTypes[T] | null
  ): boolean
  dispatchCustomEvent<T extends keyof ApiEventTypes>(
    type: T,
    detail?: ApiEventTypes[T]
  ): boolean {
    const event =
      detail === undefined
        ? new CustomEvent(type)
        : new CustomEvent(type, { detail })
    return super.dispatchEvent(event)
  }

  /** @deprecated Use {@link dispatchCustomEvent}. */
  override dispatchEvent(event: never): boolean {
    return super.dispatchEvent(event)
  }

  //!nordy - _pollQueue 제거: nordy 환경에서는 로컬 ComfyUI /prompt 폴링이 무의미하고 setInterval이 멈추지 않아 메모리 누수 원인이 됨
  /**
   * Poll status  for colab and other things that don't support websockets.
   */
  //   private _pollQueue() {
  //   setInterval(async () => {
  //     try {
  //       const resp = await this.fetchApi('/prompt')
  //       const status = (await resp.json()) as StatusWsMessageStatus
  //       this.dispatchCustomEvent('status', status)
  //     } catch (error) {
  //       this.dispatchCustomEvent('status', null)
  //     }
  //   }, 1000)
  // }

  /**
   * Creates and connects a WebSocket for realtime updates (nordy version).
   * WebSocket connects to the nordy-api relay (/ws/job/:jobId), not directly to the worker.
   */
  async createSocketPromise({
    isReconnect,
    jobId
  }: {
    isReconnect: boolean
    jobId: string
  }) {
    this.closeSocket()

    //!nordy - nordy-api 릴레이로 접속. 워커 주소는 서버가 해석(브라우저는 jobId만 안다).
    const apiBaseUrl = import.meta.env.VITE_NORDY_API_URL as string
    const relayBaseUrl = apiBaseUrl.replace(/^http/, 'ws').replace(/\/$/, '')

    let opened = false
    this.socket = new WebSocket(`${relayBaseUrl}/ws/job/${jobId}`)
    this.socket.binaryType = 'arraybuffer'

    //!nordy - Promise를 open에서 resolve, close/error에서 reject하여 settle 보장
    await new Promise<void>((resolve, reject) => {
      if (this.socket) {
        this.socketEventHandlers.open = () => {
          opened = true
          if (isReconnect) {
            this.dispatchCustomEvent('reconnected')
          }
          resolve()
        }

        this.socketEventHandlers.close = async (error) => {
          console.log(`websocket close code:`, error.code)
          const retryCodes = [1001, 1006, 1011, 1012, 1013]
          if (retryCodes.includes(error.code)) {
            setTimeout(async () => {
              this.socket = null
              try {
                const { useNordyQueueStore } =
                  await import('@/nordy/store/nordyQueueStore')
                const currentJob = useNordyQueueStore()?.runningTask
                if (currentJob?.jobId) {
                  //!nordy - 재연결 실패 시 unhandled promise rejection 방지
                  this.createSocketPromise({
                    isReconnect: true,
                    jobId: currentJob.jobId
                  }).catch((err) => {
                    console.warn('[WebSocket] reconnect failed:', err.message)
                  })
                }
              } catch (err) {
                console.error('Error in websocket reconnect:', err)
              }
            }, 300)

            if (opened) {
              this.dispatchCustomEvent('status', null)
              this.dispatchCustomEvent('reconnecting')
            }
          }
          //!nordy - close 시 항상 reject하여 Promise settle 보장 (메모리 누수 방지)
          if (!opened) {
            reject(
              new Error(`WebSocket closed before open (code: ${error.code})`)
            )
          }
        }

        this.socketEventHandlers.error = (e) => {
          console.log(`websocket error:`, e)
          if (this.socket) this.socket.close()
          //!nordy - _pollQueue는 nordy 환경에서 무의미하므로 제거
          useToastStore().removeAll()
          reject(new Error('Failed to establish WebSocket connection'))
        }

        this.socketEventHandlers.message = (event) => {
          try {
            if (event.data instanceof ArrayBuffer) {
              const view = new DataView(event.data)
              const eventType = view.getUint32(0)
              switch (eventType) {
                case 1: {
                  const imageType = view.getUint32(4)
                  const imageData = event.data.slice(8)
                  let imageMime
                  switch (imageType) {
                    case 2:
                      imageMime = 'image/png'
                      break
                    case 1:
                    default:
                      imageMime = 'image/jpeg'
                      break
                  }
                  const imageBlob = new Blob([imageData], {
                    type: imageMime
                  })
                  this.dispatchCustomEvent('b_preview', imageBlob)
                  break
                }
                //!nordy - upstream 바이너리 메시지 타입 3: progress_text
                case 3: {
                  try {
                    const decoder3 = new TextDecoder()
                    const rawData = event.data.slice(4)
                    const rawView = new DataView(rawData)

                    let offset = 0
                    let promptId: string | undefined

                    if (
                      this.getClientFeatureFlags()
                        ?.supports_progress_text_metadata
                    ) {
                      const promptIdLength = rawView.getUint32(offset)
                      offset += 4
                      promptId = decoder3.decode(
                        rawData.slice(offset, offset + promptIdLength)
                      )
                      offset += promptIdLength
                    }

                    const nodeIdLength = rawView.getUint32(offset)
                    offset += 4
                    const nodeId = decoder3.decode(
                      rawData.slice(offset, offset + nodeIdLength)
                    )
                    offset += nodeIdLength
                    const text = decoder3.decode(rawData.slice(offset))

                    this.dispatchCustomEvent('progress_text', {
                      nodeId,
                      text,
                      ...(promptId !== undefined && { prompt_id: promptId })
                    })
                  } catch (e) {
                    console.warn(
                      'Failed to parse progress_text binary message',
                      e
                    )
                  }
                  break
                }
                //!nordy - upstream 바이너리 메시지 타입 4: preview_with_metadata
                case 4: {
                  const decoder4 = new TextDecoder()
                  const metadataLength = view.getUint32(4)
                  const metadataBytes = event.data.slice(8, 8 + metadataLength)
                  const metadata = JSON.parse(decoder4.decode(metadataBytes))
                  const imageData4 = event.data.slice(8 + metadataLength)
                  const imageMime4 = metadata.image_type
                  const imageBlob4 = new Blob([imageData4], {
                    type: imageMime4
                  })
                  this.dispatchCustomEvent('b_preview_with_metadata', {
                    blob: imageBlob4,
                    nodeId: metadata.node_id,
                    displayNodeId: metadata.display_node_id,
                    parentNodeId: metadata.parent_node_id,
                    realNodeId: metadata.real_node_id,
                    jobId: metadata.prompt_id
                  })
                  this.dispatchCustomEvent('b_preview', imageBlob4)
                  break
                }
                default:
                  throw new Error(
                    `Unknown binary websocket message of type ${eventType}`
                  )
              }
            } else {
              const msg = JSON.parse(event.data) as ApiMessageUnion
              switch (msg.type) {
                case 'status':
                  if (msg.data.sid) {
                    //!nordy - clientId는 서버로부터 받지 않고 초기화 시 직접 생성
                  }
                  this.dispatchCustomEvent('status', msg.data.status ?? null)
                  break
                case 'executing':
                  this.dispatchCustomEvent(
                    'executing',
                    msg.data.display_node || msg.data.node
                  )
                  break
                case 'execution_start':
                case 'execution_error':
                case 'execution_interrupted':
                case 'execution_cached':
                case 'execution_success':
                case 'progress':
                case 'executed':
                case 'graphChanged':
                case 'promptQueued':
                case 'logs':
                case 'b_preview':
                  this.dispatchCustomEvent(msg.type, msg.data)
                  break
                default:
                  if (this._registered.has(msg.type)) {
                    super.dispatchEvent(
                      new CustomEvent(msg.type, { detail: msg.data })
                    )
                  } else if (!this.reportedUnknownMessageTypes.has(msg.type)) {
                    this.reportedUnknownMessageTypes.add(msg.type)
                    throw new Error(`Unknown message type ${msg.type}`)
                  }
              }
            }
          } catch (error) {
            console.warn('Unhandled message:', event.data, error)
          }
        }

        this.socket.addEventListener('open', this.socketEventHandlers.open)
        this.socket.addEventListener('close', this.socketEventHandlers.close)
        this.socket.addEventListener('error', this.socketEventHandlers.error)
        this.socket.addEventListener(
          'message',
          this.socketEventHandlers.message
        )
      } else {
        //!nordy - socket이 null이면 즉시 reject
        reject(new Error('Socket is null'))
      }
    })
  }

  /**
   * Initialises sockets and realtime updates.
   * In nordy architecture, WebSocket is not connected at init time.
   * It connects dynamically when a job is assigned to a worker via queuePrompt.
   */
  init() {
    //!nordy - 시작 시 자동 WebSocket 연결 없음
  }

  /**
   * Gets a list of extension urls
   */
  async getExtensions(): Promise<ExtensionsResponse> {
    const resp = await this.fetchApi('/extensions', { cache: 'no-store' })
    return await resp.json()
  }

  /**
   * Gets the available workflow templates from custom nodes.
   * @returns A map of custom_node names and associated template workflow names.
   */
  async getWorkflowTemplates(): Promise<{
    [customNodesName: string]: string[]
  }> {
    const res = await this.fetchApi('/workflow_templates')
    return await res.json()
  }

  /**
   * Gets the index of core workflow templates.
   * @param locale Optional locale code (e.g., 'en', 'fr', 'zh') to load localized templates
   */
  async getCoreWorkflowTemplates(
    locale?: string
  ): Promise<WorkflowTemplates[]> {
    const fileName =
      locale && locale !== 'en' ? `index.${locale}.json` : 'index.json'
    try {
      const res = await axios.get(this.fileURL(`/templates/${fileName}`))
      const contentType = res.headers['content-type']
      return contentType?.includes('application/json') ? res.data : []
    } catch (error) {
      // Fallback to default English version if localized version doesn't exist
      if (locale && locale !== 'en') {
        console.warn(
          `Localized templates for '${locale}' not found, falling back to English`
        )
        return this.getCoreWorkflowTemplates()
      }
      console.error('Error loading core workflow templates:', error)
      return []
    }
  }

  /**
   * Gets a list of embedding names
   */
  async getEmbeddings(): Promise<EmbeddingsResponse> {
    const resp = await this.fetchApi('/embeddings', { cache: 'no-store' })
    return await resp.json()
  }

  /**
   * Loads node object definitions for the graph
   * @returns The node definitions
   */
  async getNodeDefs(): Promise<Record<string, ComfyNodeDef>> {
    const resp = await this.fetchApi('/object_info', {
      cache: 'no-cache'
    })
    return await resp.json()
  }
  /**
   * Queues a prompt to be executed
   * @param {number} number The index at which to queue the prompt, passing -1 will insert the prompt at the front of the queue
   * @param {object} data The prompt data to queue
   * @param {QueuePromptOptions} options Optional execution options
   * @throws {PromptExecutionError} If the prompt fails to execute
   */
  async queuePrompt(
    //!nordy - number 미사용, nordy 큐는 인덱스 기반 순서를 사용하지 않음
    // number: number,
    _: number,
    data: { output: ComfyApiWorkflow; workflow: ComfyWorkflowJSON },
    options?: QueuePromptOptions
  ): Promise<PromptResponse> {
    const { output: prompt, workflow } = data
    const promptJson = JSON.stringify({
      output: prompt,
      workflow
    })

    const { useNordyWorkflowStore } =
      await import('@/nordy/store/nordyWorkflowStore')
    const { useNordyQueueStore } = await import('@/nordy/store/nordyQueueStore')
    //!nordy - 결과물 PNG 메타데이터 임베드 설정값을 job 생성 dto에 주입
    const { useNordyMetadataEmbedStore } =
      await import('@/nordy/store/nordyMetadataEmbedStore')
    const workflowStore = useNordyWorkflowStore()
    const queueStore = useNordyQueueStore()
    const metadataEmbedStore = useNordyMetadataEmbedStore()
    const workflowId = workflowStore.activeWorkflow?.workflowId
    const pendingTaskId = options?.pendingTaskId

    const findTask = () =>
      pendingTaskId
        ? queueStore.tasks.find((t) => t.id === pendingTaskId)
        : undefined

    try {
      const dto = {
        prompt: promptJson,
        clientId: this.clientId as string,
        workflow: workflowId && isObjectId(workflowId) ? workflowId : undefined,
        //!nordy - true면 결과 PNG에 워크플로우 그래프 임베드 (사용자 설정값, 기본 ON)
        metadataEmbed: metadataEmbedStore.metadataEmbed
      }

      const job = await nordyApi.createJobApi(dto)

      const taskForQueued = findTask()
      if (taskForQueued) {
        taskForQueued.taskType = 'Queued'
        taskForQueued.jobId = job.id
        queueStore.lastJobId = job.id
      } else {
        queueStore.updateTaskToQueued({ jobId: job.id })
      }

      //!nordy - 확장(rgthree, use_everywhere 등)이 options를 전달하지 않아 pendingTaskId가 없을 수 있으므로, 없으면 취소 검사 스킵
      const connectedJob = await nordyApi.waitForJobPrompted(job.id, {
        isCancelled: pendingTaskId ? () => !findTask() : undefined
      })
      const taskForRunning = findTask()
      if (taskForRunning) {
        taskForRunning.job = connectedJob
        taskForRunning.taskType = 'Running'
      } else {
        queueStore.updateTaskToRunning({ job: connectedJob })
      }

      await this.createSocketPromise({
        isReconnect: false,
        jobId: job.id
      })

      //!nordy - 적절한 에러 메시지 표시를 위해 plain object 대신 PromptExecutionError를 throw
      if (connectedJob.promptResult.status !== 200) {
        const { error: promptError, ...rest } = connectedJob.promptResult
        throw new PromptExecutionError({
          ...rest,
          error: promptError ?? 'Error in queuePrompt'
        })
      }

      return { ...connectedJob.promptResult, error: '' }
    } catch (error) {
      this.closeSocket()
      const taskForError = findTask()
      if (taskForError) {
        taskForError.status = {
          status_str: 'error',
          completed: true,
          messages: []
        }
        taskForError.taskType = 'History'
      }
      //!nordy - 401은 nordyAxios 인터셉터가 로그인 모달로 처리하므로 콘솔 노이즈 억제
      if (!(axios.isAxiosError(error) && error.response?.status === 401)) {
        console.error('Error in queuePrompt:', error)
      }
      throw error
    }
  }

  /**
   * Gets the list of assets and models referenced by a prompt that would
   * need user consent before sharing.
   */
  async getShareableAssets(
    prompt: ComfyApiWorkflow,
    options?: { owned?: boolean }
  ): Promise<ShareableAssetsResponse> {
    const body: Record<string, unknown> = { workflow_api_json: prompt }
    if (options?.owned !== undefined) {
      body.owned = options.owned
    }
    const res = await this.fetchApi('/assets/from-workflow', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
    if (res.status !== 200) {
      throw new Error(`Failed to fetch shareable assets: ${res.status}`)
    }
    const data = await res.json()
    return zShareableAssetsResponse.parse(data)
  }

  /**
   * Gets a list of model folder keys (eg ['checkpoints', 'loras', ...])
   * @returns The list of model folder keys
   */
  async getModelFolders(): Promise<ModelFolderInfo[]> {
    const res = await this.fetchApi(`/experiment/models`)
    if (res.status === 404) {
      return []
    }
    const folderBlacklist = ['configs', 'custom_nodes']
    return (await res.json()).filter(
      (folder: ModelFolderInfo) => !folderBlacklist.includes(folder.name)
    )
  }

  /**
   * Gets a list of models in the specified folder
   * @param {string} folder The folder to list models from, such as 'checkpoints'
   * @returns The list of model filenames within the specified folder
   */
  async getModels(folder: string): Promise<ModelFile[]> {
    const res = await this.fetchApi(`/experiment/models/${folder}`)
    if (res.status === 404) {
      return []
    }
    return await res.json()
  }

  /**
   * Gets the metadata for a model
   * @param {string} folder The folder containing the model
   * @param {string} model The model to get metadata for
   * @returns The metadata for the model
   */
  async viewMetadata(folder: string, model: string) {
    const res = await this.fetchApi(
      `/view_metadata/${folder}?filename=${encodeURIComponent(model)}`
    )
    const rawResponse = await res.text()
    if (!rawResponse) {
      return null
    }
    try {
      return JSON.parse(rawResponse)
    } catch (error) {
      console.error(
        'Error viewing metadata',
        res.status,
        res.statusText,
        rawResponse,
        error
      )
      return null
    }
  }

  /**
   * Loads a list of items (queue or history)
   * @param {string} type The type of items to load, queue or history
   * @returns The items of the specified type grouped by their status
   */
  async getItems(type: 'queue' | 'history') {
    if (type === 'queue') {
      return this.getQueue()
    }
    return this.getHistory()
  }

  /**
   * Gets the current state of the queue.
   * Nordy uses nordyQueueStore for queue management, so this returns empty.
   */
  async getQueue(): Promise<{
    Running: JobListItem[]
    Pending: JobListItem[]
  }> {
    return { Running: [], Pending: [] }
  }

  /**
   * Gets the prompt execution history.
   * Nordy uses nordyQueueStore for history management, so this returns empty.
   */
  async getHistory(
    //!nordy - params unused, nordy uses nordyQueueStore for history
    // max_items: number = 200,
    // options?: { offset?: number },
    _max_items: number = 200,
    _options?: { offset?: number }
  ): Promise<JobListItem[]> {
    return []
  }

  /**
   * Gets detailed job info including outputs and workflow
   * @param jobId The job ID
   * @returns Full job details or undefined if not found
   */
  async getJobDetail(jobId: string): Promise<JobDetail | undefined> {
    return fetchJobDetail(this.fetchApi.bind(this), jobId)
  }

  /**
   * Gets system & device stats
   * @returns System stats such as python version, OS, per device info
   */
  async getSystemStats(): Promise<SystemStats> {
    const res = await this.fetchApi('/system_stats')
    return await res.json()
  }

  /**
   * Sends a POST request to the API
   * @param {*} type The endpoint to post to
   * @param {*} body Optional POST data
   */
  private async _postItem(type: string, body?: Record<string, unknown>) {
    try {
      await this.fetchApi('/' + type, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: body ? JSON.stringify(body) : undefined
      })
    } catch (error) {
      console.error(error)
    }
  }

  /**
   * Deletes an item from the specified list
   * @param {string} type The type of item to delete, queue or history
   * @param {number} id The id of the item to delete
   */
  async deleteItem(type: string, id: string) {
    await this._postItem(type, { delete: [id] })
  }

  /**
   * Clears the specified list
   * @param {string} type The type of list to clear, queue or history
   */
  async clearItems(type: string) {
    await this._postItem(type, { clear: true })
  }

  /**
   * Interrupts the execution of the running job. If runningJobId is provided,
   * it is included in the payload as a helpful hint to the backend.
   * @param {string | null} [runningJobId] Optional Running Job ID to interrupt
   */
  async interrupt(runningJobId: string | null) {
    await this._postItem(
      'interrupt',
      runningJobId ? { prompt_id: runningJobId } : undefined
    )
  }

  /**
   * Gets user configuration data and where data should be stored
   */
  async getUserConfig(): Promise<User> {
    return (await this.fetchApi('/users')).json()
  }

  /**
   * Creates a new user
   * @param { string } username
   * @returns The fetch response
   */
  createUser(username: string) {
    return this.fetchApi('/users', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username })
    })
  }

  /**
   * Gets all setting values for the current user
   * @returns { Promise<string, unknown> } A dictionary of id -> value
   */
  async getSettings(): Promise<Settings> {
    const resp = await this.fetchApi('/settings')

    if (resp.status == 401) {
      throw new UnauthorizedError(resp.statusText)
    }
    return await resp.json()
  }

  /**
   * Gets a setting for the current user
   * @param { string } id The id of the setting to fetch
   * @returns { Promise<unknown> } The setting value
   */
  async getSetting(id: keyof Settings): Promise<Settings[keyof Settings]> {
    return (await this.fetchApi(`/settings/${encodeURIComponent(id)}`)).json()
  }

  /**
   * Stores a dictionary of settings for the current user
   */
  async storeSettings(settings: Partial<Settings>) {
    return this.fetchApi(`/settings`, {
      method: 'POST',
      body: JSON.stringify(settings)
    })
  }

  /**
   * Stores a setting for the current user
   */
  async storeSetting(id: keyof Settings, value: Settings[keyof Settings]) {
    return this.fetchApi(`/settings/${encodeURIComponent(id)}`, {
      method: 'POST',
      body: JSON.stringify(value)
    })
  }

  /**
   * Gets a user data file for the current user
   */
  async getUserData(file: string, options?: RequestInit) {
    return this.fetchApi(`/userdata/${encodeURIComponent(file)}`, options)
  }

  /**
   * Stores a user data file for the current user
   * @param { string } file The name of the userdata file to save
   * @param { unknown } data The data to save to the file
   * @param { RequestInit & { stringify?: boolean, throwOnError?: boolean } } [options]
   * @returns { Promise<Response> }
   */
  async storeUserData(
    file: string,
    data: unknown,
    options: RequestInit & {
      overwrite?: boolean
      stringify?: boolean
      throwOnError?: boolean
      full_info?: boolean
    } = {
      overwrite: true,
      stringify: true,
      throwOnError: true,
      full_info: false
    }
  ): Promise<Response> {
    const resp = await this.fetchApi(
      `/userdata/${encodeURIComponent(file)}?overwrite=${options.overwrite}&full_info=${options.full_info}`,
      {
        method: 'POST',
        body: options?.stringify ? JSON.stringify(data) : (data as BodyInit),
        ...options
      }
    )
    if (resp.status !== 200 && options.throwOnError !== false) {
      throw new Error(
        `Error storing user data file '${file}': ${resp.status} ${(await resp).statusText}`
      )
    }

    return resp
  }

  /**
   * Deletes a user data file for the current user
   * @param { string } file The name of the userdata file to delete
   */
  async deleteUserData(file: string) {
    const resp = await this.fetchApi(`/userdata/${encodeURIComponent(file)}`, {
      method: 'DELETE'
    })
    return resp
  }

  /**
   * Move a user data file for the current user
   * @param { string } source The userdata file to move
   * @param { string } dest The destination for the file
   */
  async moveUserData(
    source: string,
    dest: string,
    options = { overwrite: false }
  ) {
    const resp = await this.fetchApi(
      `/userdata/${encodeURIComponent(source)}/move/${encodeURIComponent(dest)}?overwrite=${options?.overwrite}`,
      {
        method: 'POST'
      }
    )
    return resp
  }

  async listUserDataFullInfo(dir: string): Promise<UserDataFullInfo[]> {
    const trimmedDir = trimEnd(dir, '/')
    const resp = await this.fetchApi(
      `/userdata?dir=${encodeURIComponent(trimmedDir)}&recurse=true&split=false&full_info=true`
    )
    if (resp.status === 404) return []
    if (resp.status !== 200) {
      throw new Error(
        `Error getting user data list '${trimmedDir}': ${resp.status} ${resp.statusText}`
      )
    }
    return resp.json()
  }

  async getGlobalSubgraphData(id: string): Promise<string> {
    const resp = await api.fetchApi('/global_subgraphs/' + id)
    if (resp.status !== 200) {
      throw new Error(
        `Failed to fetch global subgraph '${id}': ${resp.status} ${resp.statusText}`
      )
    }
    const subgraph: GlobalSubgraphData = await resp.json()
    if (!subgraph?.data) {
      throw new Error(`Global subgraph '${id}' returned empty data`)
    }
    return subgraph.data as string
  }
  async getGlobalSubgraphs(): Promise<Record<string, GlobalSubgraphData>> {
    const resp = await api.fetchApi('/global_subgraphs')
    if (resp.status !== 200) return {}
    const subgraphs: Record<string, GlobalSubgraphData> = await resp.json()
    for (const [k, v] of Object.entries(subgraphs)) {
      if (!v.data) v.data = this.getGlobalSubgraphData(k)
    }
    return subgraphs
  }

  async getLogs(): Promise<string> {
    const url = isCloud ? this.apiURL('/logs') : this.internalURL('/logs')
    return (await axios.get(url)).data
  }

  async getRawLogs(): Promise<LogsRawResponse> {
    const url = isCloud
      ? this.apiURL('/logs/raw')
      : this.internalURL('/logs/raw')
    return (await axios.get(url)).data
  }

  async subscribeLogs(enabled: boolean): Promise<void> {
    const url = isCloud
      ? this.apiURL('/logs/subscribe')
      : this.internalURL('/logs/subscribe')
    return await axios.patch(url, {
      enabled,
      clientId: this.clientId
    })
  }

  async getFolderPaths(): Promise<Record<string, string[]>> {
    const response = await axios
      .get(this.internalURL('/folder_paths'))
      .catch(() => null)
    if (!response) {
      return {} // Fallback: no filesystem paths known when API unavailable
    }
    return response.data
  }

  /* Frees memory by unloading models and optionally freeing execution cache
   * @param {Object} options - The options object
   * @param {boolean} options.freeExecutionCache - If true, also frees execution cache
   */
  async freeMemory(options: { freeExecutionCache: boolean }) {
    try {
      let mode = ''
      if (options.freeExecutionCache) {
        mode = '{"unload_models": true, "free_memory": true}'
      } else {
        mode = '{"unload_models": true}'
      }

      const res = await this.fetchApi(`/free`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: mode
      })

      if (res.status === 200) {
        if (options.freeExecutionCache) {
          useToastStore().add({
            severity: 'success',
            summary: 'Models and Execution Cache have been cleared.',
            life: 3000
          })
        } else {
          useToastStore().add({
            severity: 'success',
            summary: 'Models have been unloaded.',
            life: 3000
          })
        }
      } else {
        useToastStore().add({
          severity: 'error',
          summary:
            'Unloading of models failed. Installed ComfyUI may be an outdated version.'
        })
      }
    } catch (error) {
      useToastStore().add({
        severity: 'error',
        summary: 'An error occurred while trying to unload models.'
      })
    }
  }

  /**
   * Gets the custom nodes i18n data from the server.
   *
   * @returns The custom nodes i18n data
   */
  async getCustomNodesI18n(): Promise<CustomNodesI18n> {
    return (await axios.get(this.apiURL('/i18n'))).data
  }

  /**
   * Checks if the server supports a specific feature.
   * @param featureName The name of the feature to check (supports dot notation for nested values)
   * @returns true if the feature is supported, false otherwise
   */
  serverSupportsFeature(featureName: string): boolean {
    const override = getDevOverride<boolean>(featureName)
    if (override !== undefined) return override
    return get(this.serverFeatureFlags.value, featureName) === true
  }

  /**
   * Gets a server feature flag value.
   * @param featureName The name of the feature to get (supports dot notation for nested values)
   * @param defaultValue The default value if the feature is not found
   * @returns The feature value or default
   */
  getServerFeature<T = unknown>(featureName: string, defaultValue?: T): T {
    const override = getDevOverride<T>(featureName)
    if (override !== undefined) return override
    return get(this.serverFeatureFlags.value, featureName, defaultValue) as T
  }

  /**
   * Gets all server feature flags.
   * @returns Copy of all server feature flags
   */
  getServerFeatures(): Record<string, unknown> {
    return { ...this.serverFeatureFlags.value }
  }

  async getFuseOptions(): Promise<IFuseOptions<TemplateInfo> | null> {
    try {
      const res = await axios.get(
        this.fileURL('/templates/fuse_options.json'),
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      )
      const contentType = res.headers['content-type']
      return contentType?.includes('application/json') ? res.data : null
    } catch (error) {
      console.error('Error loading fuse options:', error)
      return null
    }
  }

  closeSocket() {
    if (this.socket) {
      if (this.socketEventHandlers.open) {
        this.socket.removeEventListener('open', this.socketEventHandlers.open)
      }
      if (this.socketEventHandlers.close) {
        this.socket.removeEventListener('close', this.socketEventHandlers.close)
      }
      if (this.socketEventHandlers.error) {
        this.socket.removeEventListener('error', this.socketEventHandlers.error)
      }
      if (this.socketEventHandlers.message) {
        this.socket.removeEventListener(
          'message',
          this.socketEventHandlers.message
        )
      }
      this.socketEventHandlers = {}
      this.socket.close()
      this.socket = null
    }
  }

  //!nordy - 커스텀 노드 익스텐션(예: VHS.core.js)을 위해 nordyApi 노출
  nordyApi = nordyApi
}

export const api = new ComfyApi()
